import React, { useState } from 'react';
import { GetStaticProps, GetStaticPaths } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import DefaultErrorPage from 'next/error';
import dynamic from 'next/dynamic';

// auth & data handling
import { validateURL, virtualSalesPageConfigured } from 'lib/helpers';
// branded page
import BrandedPage from 'components/common/BrandedPage';
import BrandedPageHead from 'components/common/BrandedPageHead';
// page elements
import Loader from 'components/common/Loader';
import {
  ICompany,
  IFeaturesFeedback,
  IPageFeedback,
  IPageVirtualSales,
  ITestimonial,
} from '@types';
import { getCompanyInfo, getCompanyPage } from 'lib/firebase/pages';
import isAccountPaused from 'lib/helpers/isAccountPaused';
import PausedPublicPageDefault from 'components/public/PausedPublicPageDefault';

/**
 * Load the recording step components dynamically
 */
const CollectVideo = dynamic(
  () => import('components/public/PublicTestimonialFlow/CollectVideo'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);
const CollectUserInfo = dynamic(
  () => import('components/public/PublicTestimonialFlow/CollectUserInfo'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

/** type of recording page props */
type RecordingPageProps = {
  company: ICompany;
  features: IFeaturesFeedback;
  pageData: IPageFeedback;
  virtualSalesPageData: IPageVirtualSales;
};

const STEP = {
  collectVideo: 'collectVideo',
  collectUserInfo: 'collectUserInfo',
};

const RecordPage = ({
  company,
  features,
  pageData,
  virtualSalesPageData,
}: RecordingPageProps): JSX.Element => {
  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <Loader />;
  }

  // This includes setting the noindex header because static files always return a status 200 but the rendered not found page page should obviously not be indexed
  if (!company) {
    return (
      <>
        <Head>
          <meta name='robots' content='noindex' />
        </Head>
        <DefaultErrorPage statusCode={404} />
      </>
    );
  }

  // initialize the testimonial with a minimal default
  const [testimonial, setTestimonial] = useState<ITestimonial>({
    companyId: company.uid,
    type: 'written',
    status: 'pending',
    rating: null,
  });

  // with Ziggeo, we can only detect changes to the audio state, not the actual state itself
  // the audio should be false the first time the component loads
  // after that, it should only change when Ziggeo microphonehealth events are triggered
  const [activeStep, setActiveStep] = React.useState(STEP.collectVideo);
  // button to allow reviewers to toggle back and forth between the video and the form
  const [showButton, setShowButton] = useState(false);

  /**
   * update the active step being displayed
   */
  const changeStep = () => {
    if (STEP.collectVideo) {
      setActiveStep(STEP.collectUserInfo);
    } else {
      setActiveStep(STEP.collectVideo);
    }
  };

  // handle the transition from the recording step to the user info step
  const continueFlow = () => {
    // move on to the user info form
    setActiveStep(STEP.collectUserInfo);
    // show the button allowing users to go back to the video
    setShowButton(true);
  };

  // end the flow and take the user to the final destination
  const endFlow = () => {
    // /** take the user to the specified redirect URL or */
    // if (validateURL(pageData?.redirectURL)) {
    //   window.location.replace(pageData.redirectURL);
    // } else {
    //   /** take the user to the client landing page, if no redirect is specified */
    //   const { companySlug } = router.query;

    //   router.replace(
    //     `/[companyName]/thanks`, // pathname
    //     `/${companySlug}/thanks` // as
    //   );
    // }

    const { companySlug } = router.query;

    /** take user to the custom testimonial thank you page */
    if (virtualSalesPageConfigured(company, virtualSalesPageData)) {
      router.replace(
        `/[companyName]/testimonial-thankyou`, // pathname
        `/${companySlug}/testimonial-thankyou` // as
      );
    } else if (validateURL(pageData?.redirectURL)) {
      /** take the user to the specified redirect URL or */
      window.location.replace(pageData.redirectURL);
    } else {
      /** take the user to the client landing page, if no redirect is specified */
      router.replace(
        `/[companyName]/thanks`, // pathname
        `/${companySlug}/thanks` // as
      );
    }
  };

  const branding = company.branding;
  const name = company.name;
  return (
    <>
      <BrandedPageHead
        pageData={pageData}
        pageName={`Record a Testimonial${name ? ' for ' + name : ''}`}
      />
      <BrandedPage
        logo={branding?.logo || null}
        colorPrimary={branding?.colorPrimary || null}
        name={name || ''}
      >
        {isAccountPaused(company) ? (
          <PausedPublicPageDefault companyName={company.name} />
        ) : (
          <div>
            {/* hide the inactive step for user flow, but keep them live in the dom so that the video 
            can keep processing and we can show the same instance to the user again */}
            <div className='flex gap-4 justify-center'>
              {/* Let the user step between the recording step and the user info step, once both are available */}
              {/* {showButton && (
              <button type='button' onClick={changeStep}>
                {activeStep === STEP.collectUserInfo
                  ? 'Preview or rerecord video'
                  : 'Next step'}
              </button>
            )} */}
              {/* Recorder */}
              <div
                className={activeStep === STEP.collectUserInfo ? 'hidden' : ''}
              >
                {/* Hide recorder when on info input form step */}
                <CollectVideo
                  companyName={company.name}
                  data={pageData.testimonial}
                  features={features}
                  testimonial={testimonial}
                  setTestimonial={setTestimonial}
                  continueFn={continueFlow}
                />
              </div>
              {/* user info form */}
              <div className={activeStep === STEP.collectVideo ? 'hidden' : ''}>
                {/* Hide user info input form during recording step */}
                <div className='w-xs'>
                  <CollectUserInfo
                    company={company}
                    privacyPolicyURL={pageData?.privacyPolicyURL || null}
                    testimonial={testimonial}
                    setTestimonial={setTestimonial}
                    continueFn={endFlow}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </BrandedPage>
    </>
  );
};

// This function gets called at build time
export const getStaticProps: GetStaticProps = async ({ params }) => {
  // Get company data
  const company: ICompany = await getCompanyInfo(params.companySlug as string);

  // Get company page customization data
  // customization data for all testimonial collection flows is saved in the feedback page
  let virtualSalesPageData = null;
  let pageData = null;
  // whether or not the company has the feature permissions to display this page
  let pageIsAllowed =
    (company && company.features?.feedback?.recordTestimonial) || false;
  if (pageIsAllowed) {
    pageData = await getCompanyPage(company.uid, 'feedback');
    virtualSalesPageData = await getCompanyPage(company.uid, 'virtualsales');
  }

  // Pass the props to the page component
  return {
    props: {
      company: pageIsAllowed ? company : null,
      features: company?.features?.feedback || {},
      pageData: pageData || {},
      virtualSalesPageData: virtualSalesPageData || {},
    },
    // we will attempt to re-generate the page:
    // - when a request comes in
    // - at most once every second
    revalidate: 1,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    /**  don't pre-render any routes at build time */
    paths: [],
    /**
     * fallback: 'blocking' mode will cause unknown paths to be rendered
     * on-demand ("SSR") without the static (placeholder) fallback.
     * this is better for SEO purposes, see this discussion: https://github.com/vercel/next.js/discussions/11552
     */
    fallback: 'blocking',
  };
};

export default RecordPage;
